import React, { useEffect, useRef, useState } from 'react';
import { bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ensureCurrentUser, ensureStripeCustomer, ensurePaymentMethodCard } from '../../util/data';
import { propTypes } from '../../util/types';
import {
  isInvoiceBilledCustomer,
  invoicingAccountState,
  billingLockedFields,
  INVOICING_ACCOUNT,
  INVOICE_PAYMENT_TERM_DAYS,
} from '../../util/invoicing';
import { billingComplete, billingFromInvoicing } from '../../util/vat';
import { savePaymentMethod, deletePaymentMethod } from '../../ducks/paymentMethods.duck';
import { handleCardSetup } from '../../ducks/stripe.duck';
import { saveBillingDetails } from '../../ducks/user.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import {
  requestInvoicing,
  lookupInvoicingCompany,
  clearInvoicingRequest,
} from '../../ducks/invoicing.duck';
import {
  BillingDetailsSummary,
  InlineTextButton,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperAccountSettingsSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { BillingDetailsForm } from '../../forms';
import { billingInitialValuesForUser } from '../../forms/BillingDetailsForm/BillingDetailsForm';
import PaymentCardSection from './PaymentCardSection';
import InvoiceSection from './InvoiceSection';
import { createStripeSetupIntent, stripeCustomer, loadData } from './BillingPage.duck';

import css from './BillingPage.css';

export const INVOICE_HASH = '#invoice';

// Everything the page derives from the current user, in one place so the
// state table of the spec (§1) can be unit-tested without rendering.
export const billingPageState = currentUser => {
  const user = ensureCurrentUser(currentUser);
  const privateData = user.attributes.profile.privateData || {};
  const invoicing = privateData.invoicing || null;
  const billing = privateData.billing || billingFromInvoicing(invoicing);
  const profileComplete = billingComplete(billing);
  const isInvoiceBilled = isInvoiceBilledCustomer(user);
  const invoiceState = invoicingAccountState(user);

  const hasDefaultPaymentMethod = !!(
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id
  );
  const card = hasDefaultPaymentMethod
    ? ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.card
    : null;

  // An approved customer with a request on file is billed as that company:
  // the identity locks, the address stays editable (decision 8).
  const lockedFields = billingLockedFields(user);

  return {
    billing,
    invoicing,
    profileComplete,
    isInvoiceBilled,
    invoiceState,
    card,
    canAddCard: profileComplete && !isInvoiceBilled,
    lockedFields,
  };
};

// Whether arriving on /account/billing#invoice opens the request form.
export const initialRequestOpen = (hash, invoiceState, profileComplete) =>
  hash === INVOICE_HASH && profileComplete && invoiceState === INVOICING_ACCOUNT.ELIGIBLE;

// Stripe's billing_details for a new card: cardholder name from the form,
// email from the account, address from the billing profile (decision 3).
export const paymentParamsFor = (currentUser, billing, formValues) => {
  const b = billing || {};
  const addressMaybe = b.country
    ? {
        address: {
          country: b.country,
          postal_code: b.postalCode,
          ...(b.street ? { line1: b.street } : {}),
          ...(b.city ? { city: b.city } : {}),
        },
      }
    : {};
  return {
    payment_method_data: {
      billing_details: {
        name: formValues.name,
        email: ensureCurrentUser(currentUser).attributes.email,
        ...addressMaybe,
      },
    },
  };
};

export const BillingPageComponent = props => {
  const {
    currentUser,
    intl,
    scrollingDisabled,
    stripeCustomerFetched,
    onManageDisableScrolling,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    deletePaymentMethodInProgress,
    onCreateSetupIntent,
    onHandleCardSetup,
    onSavePaymentMethod,
    onDeletePaymentMethod,
    fetchStripeCustomer,
    onSaveBillingDetails,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
    onRequestInvoicing,
    onLookupInvoicingCompany,
    onClearInvoicingRequest,
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
  } = props;

  const [isSubmittingCard, setIsSubmittingCard] = useState(false);
  const [cardState, setCardState] = useState(null);
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const invoiceBlockRef = useRef(null);
  const billingSectionRef = useRef(null);
  const deepLinkHandled = useRef(false);

  // Used both by the deep link below and by "Edit billing details", which can
  // be clicked from section 2 while section 1 sits above the fold.
  const scrollTo = ref => {
    if (ref.current && typeof ref.current.scrollIntoView === 'function') {
      ref.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  const user = ensureCurrentUser(currentUser);
  const userLoaded = !!user.id;
  const {
    billing,
    invoicing,
    profileComplete,
    isInvoiceBilled,
    invoiceState,
    card,
    canAddCard,
    lockedFields,
  } = billingPageState(currentUser);

  // Deep link from the listing page: once the user is known, open the request
  // (when it can be opened) and bring the invoice block into view.
  useEffect(() => {
    if (deepLinkHandled.current || !userLoaded || typeof window !== 'object') return;
    deepLinkHandled.current = true;
    const { hash } = window.location;
    if (hash !== INVOICE_HASH) return;
    if (initialRequestOpen(hash, invoiceState, profileComplete)) setRequestOpen(true);
    // Section 1 is not complete yet: bring that into view instead of the
    // invoice block, which would otherwise render the "complete billing
    // details first" hint off-screen.
    scrollTo(profileComplete ? invoiceBlockRef : billingSectionRef);
  }, [userLoaded, invoiceState, profileComplete]);

  // A request outcome belongs to this visit; drop it when leaving.
  useEffect(() => () => onClearInvoicingRequest(), [onClearInvoicingRequest]);

  const handleContactSupport = () => {
    if (typeof window === 'object' && window.Intercom) window.Intercom('showSpace', 'home');
  };

  const handleCardSubmit = params => {
    setIsSubmittingCard(true);
    const { stripe, card: cardElement, formValues } = params;
    onCreateSetupIntent()
      .then(setupIntent =>
        onHandleCardSetup({
          stripe,
          card: cardElement,
          setupIntentClientSecret:
            setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null,
          paymentParams: paymentParamsFor(currentUser, billing, formValues),
        })
      )
      .then(result => onSavePaymentMethod(user.stripeCustomer, result.setupIntent.payment_method))
      .then(() => {
        fetchStripeCustomer();
        setIsSubmittingCard(false);
        setCardState('default');
      })
      .catch(error => {
        console.error(error); // eslint-disable-line no-console
        setIsSubmittingCard(false);
      });
  };

  const handleRemoveCard = () => onDeletePaymentMethod().then(() => fetchStripeCustomer());

  const lockedNote =
    lockedFields.length > 0 ? (
      <FormattedMessage
        id={
          invoicing.businessId
            ? 'BillingPage.approvedCompanyNote'
            : 'BillingPage.approvedCompanyNoteNoId'
        }
        values={{
          companyName: invoicing.companyName || billing.legalName,
          businessId: invoicing.businessId,
        }}
      />
    ) : null;

  const showBillingForm = !profileComplete || editingBilling;
  const userName = userLoaded
    ? `${user.attributes.profile.firstName} ${user.attributes.profile.lastName}`
    : '';

  const whoIsBuying = (
    <section className={css.section} ref={billingSectionRef}>
      <div className={css.sectionHead}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="BillingPage.whoIsBuyingHeading" />
        </h2>
        {profileComplete ? (
          <div className={css.sectionAction}>
            <InlineTextButton
              type="button"
              className={css.editButton}
              onClick={() => {
                setEditingBilling(!editingBilling);
                setBillingSaved(false);
              }}
            >
              <FormattedMessage id={editingBilling ? 'BillingPage.cancel' : 'BillingPage.edit'} />
            </InlineTextButton>
          </div>
        ) : null}
      </div>
      {!userLoaded ? null : showBillingForm ? (
        <BillingDetailsForm
          formId="BillingPageBilling"
          initialValues={billingInitialValuesForUser(currentUser)}
          lockedFields={lockedFields}
          lockedNote={lockedNote}
          inProgress={saveBillingDetailsInProgress}
          saveError={saveBillingDetailsError}
          onSubmit={values =>
            onSaveBillingDetails(values)
              .then(() => {
                setEditingBilling(false);
                setBillingSaved(true);
              })
              .catch(() => null)
          }
        />
      ) : (
        <>
          <BillingDetailsSummary billing={billing} />
          {lockedNote ? (
            <p className={css.lockedNote}>{lockedNote}</p>
          ) : (
            <p className={css.help}>
              <FormattedMessage id="BillingDetailsForm.intro" />
            </p>
          )}
          {billingSaved ? (
            <p className={css.saved}>
              <FormattedMessage id="BillingPage.saved" />
            </p>
          ) : null}
        </>
      )}
    </section>
  );

  const howYouPay = (
    <section className={css.section}>
      <div className={css.sectionHead}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="BillingPage.howYouPayHeading" />
        </h2>
      </div>
      <h3 className={css.blockTitle}>
        <FormattedMessage id="BillingPage.cardHeading" />
      </h3>
      <PaymentCardSection
        stripeCustomerFetched={stripeCustomerFetched}
        card={card}
        isInvoiceBilled={isInvoiceBilled}
        canAddCard={canAddCard}
        cardState={cardState}
        onCardStateChange={setCardState}
        onDeleteCard={handleRemoveCard}
        deletePaymentMethodInProgress={deletePaymentMethodInProgress}
        onManageDisableScrolling={onManageDisableScrolling}
        formInitialValues={{ name: userName }}
        onSubmitCard={handleCardSubmit}
        submitInProgress={isSubmittingCard}
        addPaymentMethodError={addPaymentMethodError}
        deletePaymentMethodError={deletePaymentMethodError}
        createStripeCustomerError={createStripeCustomerError}
        handleCardSetupError={handleCardSetupError}
      />
      <h3 className={css.blockTitle}>
        <FormattedMessage id="BillingPage.invoiceHeading" />
      </h3>
      <div ref={invoiceBlockRef}>
        {userLoaded ? (
          <InvoiceSection
            intl={intl}
            state={invoiceState}
            profileComplete={profileComplete}
            billing={billing}
            invoicing={invoicing}
            currentUser={currentUser}
            paymentTermDays={INVOICE_PAYMENT_TERM_DAYS}
            requestOpen={requestOpen}
            onToggleRequest={setRequestOpen}
            onEditBilling={() => {
              setEditingBilling(true);
              scrollTo(billingSectionRef);
            }}
            onContactSupport={handleContactSupport}
            onLookupCompany={onLookupInvoicingCompany}
            lookupInProgress={lookupInvoicingCompanyInProgress}
            lookupResult={lookupInvoicingCompanyResult}
            lookupError={lookupInvoicingCompanyError}
            onSubmitRequest={payload =>
              onRequestInvoicing(payload).then(result => {
                setRequestOpen(false);
                return result;
              })
            }
            requestInProgress={requestInvoicingInProgress}
            requestError={requestInvoicingError}
            requestResult={requestInvoicingResult}
          />
        ) : null}
      </div>
    </section>
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'BillingPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="BillingPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperAccountSettingsSideNav currentTab="BillingPage" currentUser={currentUser} />
        <LayoutWrapperMain>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="BillingPage.heading" />
            </h1>
            {whoIsBuying}
            {howYouPay}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

BillingPageComponent.defaultProps = {
  currentUser: null,
  addPaymentMethodError: null,
  deletePaymentMethodError: null,
  createStripeCustomerError: null,
  handleCardSetupError: null,
  saveBillingDetailsError: null,
  requestInvoicingError: null,
  requestInvoicingResult: null,
  lookupInvoicingCompanyError: null,
  lookupInvoicingCompanyResult: null,
};

BillingPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  scrollingDisabled: bool.isRequired,
  stripeCustomerFetched: bool.isRequired,
  onManageDisableScrolling: func.isRequired,
  addPaymentMethodError: object,
  deletePaymentMethodError: object,
  createStripeCustomerError: object,
  handleCardSetupError: object,
  deletePaymentMethodInProgress: bool.isRequired,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onDeletePaymentMethod: func.isRequired,
  fetchStripeCustomer: func.isRequired,
  onSaveBillingDetails: func.isRequired,
  saveBillingDetailsInProgress: bool.isRequired,
  saveBillingDetailsError: propTypes.error,
  onRequestInvoicing: func.isRequired,
  onLookupInvoicingCompany: func.isRequired,
  onClearInvoicingRequest: func.isRequired,
  requestInvoicingInProgress: bool.isRequired,
  requestInvoicingError: propTypes.error,
  requestInvoicingResult: object,
  lookupInvoicingCompanyInProgress: bool.isRequired,
  lookupInvoicingCompanyError: propTypes.error,
  lookupInvoicingCompanyResult: object,
};

const mapStateToProps = state => {
  const { currentUser, saveBillingDetailsInProgress, saveBillingDetailsError } = state.user;
  const {
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = state.paymentMethods;
  const { stripeCustomerFetched } = state.BillingPage;
  const { handleCardSetupError } = state.stripe;
  const {
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
  } = state.invoicing;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    stripeCustomerFetched,
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onCreateSetupIntent: () => dispatch(createStripeSetupIntent()),
  onSavePaymentMethod: (customer, paymentMethodId) =>
    dispatch(savePaymentMethod(customer, paymentMethodId)),
  onDeletePaymentMethod: () => dispatch(deletePaymentMethod()),
  onSaveBillingDetails: billing => dispatch(saveBillingDetails(billing)),
  onRequestInvoicing: payload => dispatch(requestInvoicing(payload)),
  onLookupInvoicingCompany: params => dispatch(lookupInvoicingCompany(params)),
  onClearInvoicingRequest: () => dispatch(clearInvoicingRequest()),
});

const BillingPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(BillingPageComponent);

BillingPage.loadData = loadData;

export default BillingPage;
